import { Button, Dropdown, Menu, Tooltip } from 'antd';
import './ListCommunicationsComponent.scss';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { NewsData } from '../../../models/CommunicationsData';
import { URL_IMAGE, LanguageCodeToText, NewStatus, getCountryCode } from '../../../utils/constants';
import { CheckCircleOutlined, CloseCircleOutlined, DeleteOutlined, EditOutlined, EyeOutlined, MoreOutlined, StarFilled, StarOutlined } from '@ant-design/icons';
import { useEffect, useState } from 'react';
import { EditFeaturedNews } from '../../../api/repositoryEP';
import { LoadingData } from '../../common';
import TagStatusNews from '../../news/TagStatusNews';
import moment from 'moment';
import { useAppConfig } from '../../../Contexts/AppConfigContext';
import ReactCountryFlag from 'react-country-flag';
import { useSessionData } from '../../../Contexts/SessionDataContext';

interface ListCommunicationsComponentProps {
    prefixImg: string;
    list: NewsData[];
    loading: boolean;
    setDeleteModalVisible: (isVisible: boolean) => void;
    setVisibleModalUnpublishedNews: (isVisible: boolean) => void;
    setIdSelected: (id: string) => void;
    changeNewStatus(id: string, isActive: boolean): void;
}

const ListCommunicationsComponent = (props: ListCommunicationsComponentProps): JSX.Element => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const appConfig = useAppConfig();
    const userSesionData = useSessionData();
    const [newsList, setNewsList] = useState(props.list);


    const [selectedLanguages, setSelectedLanguages] = useState<Record<string, string>>({});

    useEffect(() => {
        setNewsList(props.list || []);
        const userLang = i18n.language.split("-")[0].toLowerCase();
        const initialLanguages: Record<string, string> = {};
        props.list.forEach((news) => {
            if (news.titlesByLanguage.length > 0) {
                const preferred = news.titlesByLanguage.find(
                    (kv) => kv.key.split("-")[0].toLowerCase() === userLang
                );
                initialLanguages[news.id] = preferred?.key ?? news.titlesByLanguage[0].key;
            }
        });
        setSelectedLanguages(initialLanguages);
    }, [props.list, i18n.language]);

    const buildImgUrl = (img: string): string => {
        // el prefijo y/o la imagen pueden venir ya con el protocolo (incluso mal formado,
        // tipo "https//"), así que quitamos todos los que haya delante y ponemos uno solo
        const path = `${props.prefixImg ?? ""}${img}`.replace(/^(?:https?:?\/\/)+/i, "");
        return `https://${path}?id=${Math.random()}`;
    };

    const openDeleteModal = (id: string) => {
        if (id) {
            props.setIdSelected(id); // Configura el ID
            props.setDeleteModalVisible(true); // Abre el modal
        }
    };



    const openModalUnpublishedNews = (id?: string) => {
        if (id) {
            props.setIdSelected(id); // Configura el ID
            props.setVisibleModalUnpublishedNews(true); // Abre el modal
        }
    };

    const editNew = (id: string) => {
        navigate(`${window.location.pathname}/new/${id}`);
    };


    const seeNewsDetails = (id: string) => {
        navigate(`${window.location.pathname}/new-details/${id}`);
    };


    const changeNewStatus = (id: string, isActive: boolean) => {
        props.changeNewStatus(id, isActive);
    };




    const handleFeaturedNews = (id: string, isActive: boolean) => {
        EditFeaturedNews(id, isActive).then((response) => {
            if (response?.type == 1 && response.data == true) {
                setNewsList((prevList) =>
                    prevList.map((news) =>
                        news.id === id ? { ...news, isFeatured: isActive } : news
                    )
                );
            }
        });
    };


    const menu = (id: string, status: NewStatus) => (
        <Menu>
            <Menu.Item key="1" onClick={() => seeNewsDetails(id)} icon={<EyeOutlined />}>
                {t('components.communications.see')}
            </Menu.Item>
            <Menu.Item key="2" onClick={() => editNew(id)} icon={<EditOutlined />}>
                {t('components.communications.edit')}
            </Menu.Item>
            {status !== "active" && (
                <Menu.Item key="3" onClick={() => changeNewStatus(id, true)} icon={<CheckCircleOutlined />}>
                    {t('components.communications.publish')}
                </Menu.Item>
            )}
            {status === "active" && (
                <Menu.Item key="4" onClick={() => openModalUnpublishedNews(id)} icon={<CloseCircleOutlined />}>
                    {t('components.communications.unpublish')}
                </Menu.Item>
            )}
            <Menu.Item key="5" danger onClick={() => openDeleteModal(id)} icon={<DeleteOutlined />}>
                {t('components.communications.delete')}
            </Menu.Item>
        </Menu>
    );

    const ThreeDotsMenu = ({ id, status }: { id: string; status: NewStatus }) => (
        <Dropdown overlay={menu(id, status)} trigger={["click"]}>
            <Button type="text" icon={<MoreOutlined className="three-dots-icon" />} />
        </Dropdown>
    );




    const changeLanguage = (newsId: string, languageKey: string) => {
        setSelectedLanguages((prev) => ({ ...prev, [newsId]: languageKey }));
    };

    const renderButtons = (newsId: string, titlesByLanguage: { key: string; value: string }[]) => (
        <div className="language-buttons-container">
            {titlesByLanguage.map((kv) => (
                <Button
                    className='language-button'
                    key={kv.key}
                    type={selectedLanguages[newsId] === kv.key ? 'primary' : 'default'}
                    onClick={() => changeLanguage(newsId, kv.key)}
                >
                     <ReactCountryFlag countryCode={getCountryCode(kv.key)} svg /> {t(LanguageCodeToText.find((item) => item.key === kv.key)?.label || "")}
                </Button>
            ))}
        </div>
    );

    return (
        <div className="list-communications">
            {props.loading && <LoadingData />}
            {!props.loading &&
                newsList.map((f) => {
                    const currentLanguage = selectedLanguages[f.id] || f.titlesByLanguage[0]?.key;
                    const currentTitle = f.titlesByLanguage.find((kv) => kv.key === currentLanguage)?.value || "";

                    return (
                        <div key={f.id} className="news-card">

                            {/* Imagen */}
                            <div className="news-card__image">
                                <img
                                    src={f.img ? buildImgUrl(f.img) : `${URL_IMAGE}no-image.jpg`}
                                    alt={currentTitle}
                                />
                            </div>

                            {/* Contenido */}
                            <div className="news-card__content">

                                {/* Cabecera: idiomas + acciones */}
                                <div className="news-card__top">
                                    <div className="news-card__langs">
                                        {f.titlesByLanguage.map((kv) => (
                                            <button
                                                key={kv.key}
                                                className={`news-lang-btn${selectedLanguages[f.id] === kv.key ? ' news-lang-btn--active' : ''}`}
                                                onClick={() => changeLanguage(f.id, kv.key)}
                                            >
                                                <ReactCountryFlag countryCode={getCountryCode(kv.key)} svg style={{ marginRight: 4 }} />
                                                {t(LanguageCodeToText.find((item) => item.key === kv.key)?.label || '')}
                                            </button>
                                        ))}
                                    </div>
                                    <div className="news-card__actions">
                                        <Tooltip title={f.isFeatured ? t('components.communications.unfeature-news') : t('components.communications.feature-news')}>
                                            {f.isFeatured
                                                ? <StarFilled onClick={() => handleFeaturedNews(f.id, false)} className="news-card__star news-card__star--active" />
                                                : <StarOutlined onClick={() => handleFeaturedNews(f.id, true)} className="news-card__star" />}
                                        </Tooltip>
                                        <ThreeDotsMenu id={f.id} status={f.status} />
                                    </div>
                                </div>

                                {/* Título */}
                                <div className="news-card__title">{currentTitle || <span className="news-card__no-title">—</span>}</div>

                                {/* Footer: estado + fecha */}
                                <div className="news-card__footer">
                                    <TagStatusNews status={f.status} sheduleDayFrom={f.sheduleDayFrom} sheduleDayTo={f.sheduleDayTo} />
                                    {f.status === 'active' && (
                                        <span className="news-card__date">
                                            <span className="news-card__date-label">{t('components.communications.date')}</span>
                                            {moment.utc(f.sheduleDayFrom).local().format(appConfig.config.dateFormat)}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
        </div>
    );
};

export default ListCommunicationsComponent;